// src/pages/RemindersPage.tsx
import React, { useState } from "react";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, List, Plus } from "lucide-react";

interface Reminder {
  id: string;
  title: string;
  subtitle?: string;
  date: Date;
  color: "purple" | "blue" | "red" | "yellow" | "green";
}

const colorToBg = (color: Reminder["color"]): string => {
  const map: Record<Reminder["color"], string> = {
    purple: "bg-purple-500",
    blue: "bg-blue-500",
    red: "bg-red-500",
    yellow: "bg-yellow-500",
    green: "bg-green-500",
  };
  return map[color];
};

const RemindersPage: React.FC = () => {
  const [viewMode, setViewMode] = useState<"calendar" | "list">("calendar");
  const [currentDate, setCurrentDate] = useState(new Date(2026, 1)); // February 2026

  const reminders: Reminder[] = [
    { id: "1", title: "Close Tariff Audit", date: new Date(2026, 1, 1), color: "purple" },
    { id: "2", title: "SE-A Breach Variant", date: new Date(2026, 1, 3), color: "blue" },
    { id: "3", title: "Li-Ori Delay Monit.", subtitle: "HAZMAT Waiver...", date: new Date(2026, 1, 26), color: "red" },
    { id: "4", title: "Southeast Asia Wait", date: new Date(2026, 1, 26), color: "blue" },
    { id: "5", title: "En-Import Duty Chg", date: new Date(2026, 2, 4), color: "red" },
    { id: "6", title: "Place order to Supplier A", date: new Date(2026, 2, 9), color: "yellow" },
    { id: "7", title: "Place order to Supplier B", date: new Date(2026, 2, 14), color: "green" },
    { id: "8", title: "Review Q1 shipments", date: new Date(2026, 2, 19), color: "purple" },
  ];

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek };
  };

  const getRemindersForDate = (day: number) => {
    return reminders.filter((reminder) => {
      const reminderDate = reminder.date;
      return (
        reminderDate.getDate() === day &&
        reminderDate.getMonth() === currentDate.getMonth() &&
        reminderDate.getFullYear() === currentDate.getFullYear()
      );
    });
  };

  const navigateMonth = (direction: "prev" | "next") => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      if (direction === "prev") {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
  };

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentDate);
  const calendarDays = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const emptyDays = Array.from({ length: startingDayOfWeek }, (_, i) => i);

  const getColorClasses = (color: Reminder["color"]) => {
    switch (color) {
      case "purple":
        return "bg-purple-50 text-purple-700";
      case "blue":
        return "bg-blue-50 text-blue-700";
      case "red":
        return "bg-red-50 text-red-700";
      case "yellow":
        return "bg-yellow-50 text-yellow-800";
      case "green":
        return "bg-green-50 text-green-700";
    }
  };

  /** Reference “today” cell (Feb 26 in mock). */
  const isTodayCell = (day: number) => day === 26 && currentDate.getMonth() === 1 && currentDate.getFullYear() === 2026;

  return (
    <div className="w-full min-w-0 max-w-full px-6 py-6 pb-8 font-sans sm:px-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-wl-black">Reminders</h1>
        <p className="mt-1 text-sm leading-snug text-gray-600">
          Manage shipment alerts, monitoring rules, and automated notifications
        </p>
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-3 sm:mb-5">
        <fieldset
          className="flex shrink-0 items-center gap-2.5 border-0 p-0 m-0"
          aria-label="View mode"
        >
          <button
            type="button"
            onClick={() => setViewMode("calendar")}
            className={`flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold shadow-sm transition-colors ${
              viewMode === "calendar"
                ? "border-wl-red bg-wl-red text-white"
                : "border-wl-border bg-white text-gray-600 hover:bg-wl-bg-soft"
            }`}
          >
            <CalendarIcon className="size-4 shrink-0" strokeWidth={2} />
            Calendar
          </button>
          <button
            type="button"
            onClick={() => setViewMode("list")}
            className={`flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold shadow-sm transition-colors ${
              viewMode === "list"
                ? "border-wl-red bg-wl-red text-white"
                : "border-wl-border bg-white text-gray-600 hover:bg-wl-bg-soft"
            }`}
          >
            <List className="size-4 shrink-0" strokeWidth={2} />
            List
          </button>
        </fieldset>

        <button
          type="button"
          className="inline-flex items-center gap-2 rounded-lg bg-wl-red px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors duration-200 hover:bg-[#D41F2E] active:bg-[#C11A28] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-wl-red focus-visible:ring-offset-2"
        >
          <Plus className="size-4 shrink-0" strokeWidth={2.5} />
          Create Reminder
        </button>
      </div>

      {viewMode === "calendar" && (
        <div className="w-full rounded-xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
          <h2 className="mb-3 text-lg font-semibold text-black">Reminder Calendar</h2>
          <div className="relative mb-6 flex min-h-10 items-center justify-center">
            <button
              type="button"
              onClick={() => navigateMonth("prev")}
              className="absolute left-0 top-1/2 flex size-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md bg-transparent text-gray-500 appearance-none transition-colors hover:text-wl-black focus:outline-none focus-visible:ring-2 focus-visible:ring-wl-red focus-visible:ring-offset-0"
              style={{
                backgroundColor: "transparent",
                WebkitAppearance: "none",
                MozAppearance: "none",
                appearance: "none",
              }}
              aria-label="Previous month"
            >
              <ChevronLeft className="size-5" strokeWidth={2} />
            </button>
            <h3 className="text-center text-base font-semibold text-black px-12 sm:px-14">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h3>
            <button
              type="button"
              onClick={() => navigateMonth("next")}
              className="absolute right-0 top-1/2 flex size-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md bg-transparent text-gray-500 appearance-none transition-colors hover:text-wl-black focus:outline-none focus-visible:ring-2 focus-visible:ring-wl-red focus-visible:ring-offset-0"
              style={{
                backgroundColor: "transparent",
                WebkitAppearance: "none",
                MozAppearance: "none",
                appearance: "none",
              }}
              aria-label="Next month"
            >
              <ChevronRight className="size-5" strokeWidth={2} />
            </button>
          </div>

          <div className="rounded-lg border border-gray-200 overflow-hidden bg-gray-200">
            <div className="grid grid-cols-7 gap-px bg-gray-200">
              {dayNames.map((day) => (
                <div
                  key={day}
                  className="px-2 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500 bg-gray-50"
                >
                  {day}
                </div>
              ))}

              {emptyDays.map((i) => (
                <div key={`empty-${i}`} className="min-h-[112px] bg-white" aria-hidden />
              ))}

              {calendarDays.map((day) => {
                const dayReminders = getRemindersForDate(day);
                const today = isTodayCell(day);

                return (
                  <div
                    key={day}
                    data-testid={`reminder-calendar-day-${day}`}
                    className={`min-h-[112px] p-2 flex flex-col ${today ? "bg-red-50" : "bg-white"}`}
                  >
                    <div className="flex justify-end mb-1.5 shrink-0">
                      <span
                        className={
                          today
                            ? "flex size-7 items-center justify-center rounded-full bg-wl-red text-xs font-semibold text-white"
                            : "text-xs font-medium text-gray-800"
                        }
                      >
                        {day}
                      </span>
                    </div>
                    <div className="flex flex-col gap-1.5 min-h-0 flex-1">
                      {dayReminders.map((reminder) => (
                        <div
                          key={reminder.id}
                          className={`rounded-md px-2 py-1.5 text-left text-xs font-medium leading-snug ${getColorClasses(
                            reminder.color
                          )}`}
                        >
                          {reminder.title}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {viewMode === "list" && (
        <div className="w-full rounded-xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
          <h2 className="mb-6 text-lg font-semibold text-black">All Reminders</h2>
          <div className="divide-y divide-gray-100">
            {reminders.map((reminder) => (
              <div key={reminder.id} className="flex items-start gap-3 py-4 first:pt-0">
                <div
                  className={`mt-1.5 size-2 shrink-0 rounded-full ${
                    colorToBg(reminder.color)
                  }`}
                />
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-medium text-gray-900">{reminder.title}</h3>
                  {reminder.subtitle && (
                    <p className="text-xs text-gray-500 mt-0.5">{reminder.subtitle}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    {reminder.date.toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RemindersPage;
